import React, { createContext, useContext, useLayoutEffect, useState } from "react";

/**
 * Whether the page on screen draws the application footer itself, inside its own layout, rather than
 * the shell drawing it under the whole page.
 *
 * The shell's footer spans the page's full width, so a page with a column of its own beside its
 * content — the proposal pages' Comments pane — could only end that column above the footer. The
 * footer's links reach under the column at 1280px, and a column run past them covered "Contact Us"
 * (#4964); a column stopped short of them left the footer's band empty under the pane, read as 70px of
 * padding below the composer. A page that takes the footer can set it beside such a column instead.
 */
type PageFooterPlacement = {
  pagePlacesFooter: boolean;
  setPagePlacesFooter: (placesFooter: boolean) => void;
};

const PageFooterPlacementContext = createContext<PageFooterPlacement>({
  pagePlacesFooter: false,
  setPagePlacesFooter: () => undefined,
});

export function PageFooterPlacementProvider(props: Readonly<{ children: React.ReactNode }>) {
  const [pagePlacesFooter, setPagePlacesFooter] = useState(false);
  const value = React.useMemo(() => ({ pagePlacesFooter, setPagePlacesFooter }), [pagePlacesFooter]);
  return <PageFooterPlacementContext.Provider value={value}>{props.children}</PageFooterPlacementContext.Provider>;
}

/** The shell asks: does the page on screen draw the footer itself? */
export function usePagePlacesFooterAnswer(): boolean {
  return useContext(PageFooterPlacementContext).pagePlacesFooter;
}

/**
 * A page calls this while it is mounted and draws `<Footer />` where its layout wants it. Set before
 * the browser paints, so the footer is never drawn twice, and handed back when the page goes.
 */
export function usePagePlacesFooter(): void {
  const { setPagePlacesFooter } = useContext(PageFooterPlacementContext);
  useLayoutEffect(() => {
    setPagePlacesFooter(true);
    return () => setPagePlacesFooter(false);
  }, [setPagePlacesFooter]);
}
